import * as React from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { PageHeader } from '@/components/app/PageHeader';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/app/EmptyState';
import { ErrorState } from '@/components/app/ErrorState';
import { humanizeError } from '@/lib/apiClient';
import { useAuditList } from '../hooks';
import { AuditDiffViewer } from '../components/AuditDiffViewer';
import type { AdminAuditTargetType } from '../types';

const TARGET_TYPES: AdminAuditTargetType[] = [
  'config',
  'journey',
  'user_override',
  'alert_policy',
  'alert_firing',
];

export function AuditPage() {
  const [actorId, setActorId] = React.useState('');
  const [targetType, setTargetType] = React.useState<AdminAuditTargetType | 'all'>('all');
  const [targetId, setTargetId] = React.useState('');
  // Stored as the raw <input type="datetime-local"> value (local time).
  // Converted to UTC ISO when building query params, so the browser never
  // displays a UTC timestamp as if it were local.
  const [sinceLocal, setSinceLocal] = React.useState('');
  const [cursor, setCursor] = React.useState<string | undefined>(undefined);
  const [expanded, setExpanded] = React.useState<Set<string>>(new Set());

  const params = React.useMemo(
    () => ({
      actorId: actorId.trim() || undefined,
      targetType: targetType === 'all' ? undefined : targetType,
      targetId: targetId.trim() || undefined,
      since: sinceLocal ? new Date(sinceLocal).toISOString() : undefined,
      cursor,
      limit: 50,
    }),
    [actorId, targetType, targetId, sinceLocal, cursor],
  );

  const { data, isLoading, isError, error, refetch, isFetching } = useAuditList(params);

  const toggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearFilters = () => {
    setActorId('');
    setTargetType('all');
    setTargetId('');
    setSinceLocal('');
    setCursor(undefined);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Notifications · Audit"
        description="Every admin mutation, forever (90-day retention). Expand a row to view before/after diff."
      />

      <Card>
        <CardContent className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-1.5">
            <Label>Actor id / email</Label>
            <Input value={actorId} onChange={(e) => setActorId(e.target.value)} placeholder="admin@…" />
          </div>
          <div className="space-y-1.5">
            <Label>Target type</Label>
            <Select value={targetType} onValueChange={(v) => setTargetType(v as AdminAuditTargetType | 'all')}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {TARGET_TYPES.map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Target id</Label>
            <Input
              value={targetId}
              onChange={(e) => setTargetId(e.target.value)}
              placeholder="journey id / user id / policy key"
              className="font-mono"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Since</Label>
            <Input
              type="datetime-local"
              value={sinceLocal}
              onChange={(e) => setSinceLocal(e.target.value)}
            />
          </div>
          <div className="sm:col-span-2 lg:col-span-4 flex items-center justify-end gap-2">
            <Button variant="outline" size="sm" onClick={clearFilters}>Clear filters</Button>
            <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {isError && (
        <ErrorState title="Audit log unavailable" description={humanizeError(error)} actionLabel="Retry" onAction={() => refetch()} />
      )}

      <Card>
        <CardHeader>
          <CardTitle>Entries</CardTitle>
          <CardDescription>Ordered newest first.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-1 p-0">
          {isLoading && (
            <div className="space-y-2 p-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          )}
          {!isLoading && data?.entries.length === 0 && (
            <div className="p-6">
              <EmptyState
                title="No audit entries match these filters"
                description="Try widening your filters, or check if the mutation actually landed."
              />
            </div>
          )}
          {data?.entries.map((entry) => {
            const open = expanded.has(entry.auditId);
            return (
              <div key={entry.auditId} className="border-t first:border-t-0">
                <button
                  type="button"
                  onClick={() => toggle(entry.auditId)}
                  className="flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-muted/40 focus:outline-none focus-visible:bg-muted/60"
                  aria-expanded={open}
                >
                  {open ? <ChevronDown className="mt-1 h-4 w-4 shrink-0" /> : <ChevronRight className="mt-1 h-4 w-4 shrink-0" />}
                  <div className="flex-1 space-y-0.5">
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="font-mono text-xs text-muted-foreground">{entry.createdAt}</span>
                      <Badge variant="muted">{entry.action}</Badge>
                      <span className="text-muted-foreground">{entry.targetType}</span>
                      <span className="font-mono text-xs">{entry.targetId}</span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      <span className="font-medium text-foreground">{entry.actorEmail || entry.actorId}</span>
                      {entry.reason && <> — "{entry.reason}"</>}
                    </div>
                  </div>
                </button>
                {open && (
                  <div className="border-t bg-muted/10 p-4">
                    <AuditDiffViewer entry={entry} />
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>

      <div className="flex items-center justify-end gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={!cursor}
          onClick={() => setCursor(undefined)}
        >
          Back to latest
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={!data?.nextCursor}
          onClick={() => data?.nextCursor && setCursor(data.nextCursor)}
        >
          Next page
        </Button>
      </div>
    </div>
  );
}
